import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  Image,
  SimpleGrid,
  VStack,
  Button,
  Flex,
  HStack,
  Tag,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  useDisclosure,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabaseClient";
import SendEmail from "../../components/SendEmail";
import { useNavigate } from "react-router-dom";

// 토큰 갱신 함수
export const refreshAccessToken = async () => {
  try {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      throw new Error("No refresh token");
    }

    const response = await fetch(`https://plpl.rhoonart.co.kr/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    const result = await response.json();
    if (result?.data?.access_token) {
      localStorage.setItem("access_token", result.data.access_token);
      return result.data.access_token;
    } else {
      // refresh_token도 만료/실패 시
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      window.location.href = "/laeeblyforshorts";
      throw new Error("Failed to refresh token");
    }
  } catch (error) {
    console.error("Token refresh failed:", error);
    // refresh token도 만료된 경우 /laeeblyforshorts 이동
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    window.location.href = "/laeeblyforshorts";
    throw error;
  }
};

const CollaborationHome = () => {
  const { user } = useAuth();
  const bgGradient = useColorModeValue(
    "linear(to-b, blue.50, white)",
    "linear(to-b, gray.900, gray.800)"
  );
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [form, setForm] = React.useState({
    name: "",
    email: "",
    phone: "",
    channel: "",
    agree: false,
  });
  const [isLoading, setIsLoading] = React.useState(false);
  const [loading, setLoading] = useState(true);
  const [images, setImages] = useState([]);
  const navigate = useNavigate();

  const isFormValid =
    form.name && form.email && form.phone && form.channel && form.agree;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    console.log("handleSubmit 시작");
    setIsLoading(true);
    try {
      console.log("계약 확인 시작");
      // 계약 여부 확인
      const { data: contractData, error: contractError } = await supabase
        .from("shorts_contract")
        .select("id")
        .eq("contractor", user.email)
        .maybeSingle();

      if (contractError) {
        console.error("계약 확인 중 오류 발생:", contractError);
        throw contractError;
      }

      if (contractData) {
        console.log("이미 계약된 회원");
        toast({
          title: "이미 계약하신 회원입니다.",
          description: "계약된 회원은 상담 신청이 불필요합니다.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        onClose();
        return;
      }

      await SendEmail({
        email: "contact@plpl.kr",
        ccEmails: user?.email ? [user.email] : [],
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      await SendEmail({
        email: "jaeeui.kwon@rhoonart.com",
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      await SendEmail({
        email: "blair.park@laeebly.com",
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      await SendEmail({
        email: "kirby.lee@laeebly.com",
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      console.log("이메일 전송 완료");
      toast({
        title: "상담 요청이 접수되었습니다.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setForm({ name: "", email: "", phone: "", channel: "", agree: false });
      onClose();
    } catch (e) {
      console.error("상담 신청 중 오류 발생:", e);
      toast({
        title: "상담 요청에 실패했습니다.",
        description: "잠시 후 다시 시도해주세요.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      console.log("handleSubmit 종료");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchAndStoreTokens = async () => {
      if (!user?.email) return;
      try {
        // 1. user 테이블에서 id 조회
        const { data, error } = await supabase
          .from("user")
          .select("id")
          .eq("email", user.email)
          .single();

        if (error || !data?.id) return;
        const uniqueId = data.id;

        // 2. 토큰 요청
        const response = await fetch(`https://plpl.rhoonart.co.kr/auth/token`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userId: uniqueId }),
        });

        const result = await response.json();
        if (result?.data?.access_token && result?.data?.refresh_token) {
          localStorage.setItem("access_token", result.data.access_token);
          localStorage.setItem("refresh_token", result.data.refresh_token);
        }
      } catch (e) {
        console.error("Token fetch failed:", e);
      }
    };
    fetchAndStoreTokens();
  }, [user]);

  useEffect(() => {
    const fetchImages = async () => {
      const fetchedImages = [
        "/images/collaboration/main-visual.png",
        "/images/music1.png",
        "/images/music2.png",
        "/images/music3.png",
        "/images/music4.png",
      ];
      // 실제로는 fetch 등 비동기 로직 필요
      setImages(fetchedImages);
      setLoading(false);
    };
    fetchImages();
  }, []);

  if (loading) {
    return (
      <Flex minH="60vh" align="center" justify="center">
        <Spinner size="xl" color="teal.500" />
      </Flex>
    );
  }

  return (
    <Box bgGradient={bgGradient} minH="100vh">
      {/* 대표 이미지 및 텍스트 */}
      <Container maxW="container.md" py={16}>
        <VStack spacing={6} align="center">
          <Image src={images[0]} alt="대표 이미지" boxSize="240px" mb={2} />
          <Text fontSize="md" color="gray.600" textAlign="center">
            2018년부터 17,608곡의 음반 IP 거래를 중개한
          </Text>
          <Heading size="lg" textAlign="center">
            음반 IP 전문 서비스{" "}
            <span style={{ color: "#319795" }}>레이블리</span>가<br />
            이제 <span style={{ color: "#38B2AC" }}>쇼츠 크리에이터</span>와
            함께해요
          </Heading>
          <Flex justify="center" gap={4} mt={4}>
            <Image src="/images/collaboration/phone.png" alt="폰" h="460px" />
          </Flex>
        </VStack>
      </Container>

      {/* YT프리미엄 섹션 */}
      <Box bg="blue.200" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="center">
            <Tag colorScheme="blue">#데이터 #투명성</Tag>
            <Heading>쇼츠의 YT프리미엄 시청자 데이터</Heading>
            <Image
              src="/images/play-button.png"
              alt="재생 버튼"
              h="200px"
              mb={4}
            />
            <VStack spacing={2} textAlign="center">
              <Text>일일 데이터(시청 시간, 예상 수익, 조회수)는 기본</Text>
              <Text>YT프리미엄 사용자의 조회수, 시청시간 제공</Text>
              <Text>데이터 기반 채널 운영</Text>
            </VStack>
          </VStack>
        </Container>
      </Box>

      {/* 비즈니스 모델 섹션 */}
      <Box bg="blue.400" color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="center">
            <Tag colorScheme="blue">
              #공신력 #한국저작권위원회검토완료 #한국콘텐츠진흥원지원사업선정
            </Tag>
            <Heading size="lg">이 모든게 가능한 이유는</Heading>
            <Image
              src="/images/business-icon.png"
              alt="비즈니스 아이콘"
              h="200px"
              mb={4}
            />
            <VStack spacing={2} textAlign="center">
              <Text>레이블리의 인정받은 비즈니스 모델</Text>
              <Text>저작인접권료 수익 정규권을 제공하고</Text>
              <Text>음원의 수익을 드리기 때문</Text>
            </VStack>
          </VStack>
        </Container>
      </Box>

      {/* 채팅 그만 섹션 */}
      <Box bg="blue.500" color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="center">
            <Tag colorScheme="blue">#24시간 #7일내내 #언제든지</Tag>
            <Image
              src="/images/chat-icon.png"
              alt="채팅 아이콘"
              h="200px"
              mb={4}
            />
            <VStack spacing={2} textAlign="center">
              <Text>02, 010 유선 전화</Text>
              <Text>레이블리 채널톡</Text>
              <Text>레이블리 카카오톡</Text>
              <Text>필요할 때, 필요한 서비스</Text>
            </VStack>
            <HStack
              bg="white"
              spacing={8}
              mt={4}
              size="lg"
              px={20}
              py={4}
              borderRadius="3xl"
            >
              <Image src="/images/message-chat.gif" alt="메세지톡" h="50px" />
              <Image src="/images/phone.png" alt="전화" h="50px" />
              <Image src="/images/kakao-talk.png" alt="카카오톡" h="50px" />
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* 레이블리의 음악 섹션 */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={8} align="center">
          <Heading>레이블리의 음악</Heading>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
            <Image src={images[1]} alt="앨범 1" borderRadius="lg" h="240px" />
            <Image src={images[2]} alt="앨범 2" borderRadius="lg" h="240px" />
            <Image src={images[3]} alt="앨범 3" borderRadius="lg" h="240px" />
            <Image src={images[4]} alt="앨범 4" borderRadius="lg" h="240px" />
          </SimpleGrid>
        </VStack>
      </Container>

      {/* 빠르게 성장하는 레이블리 섹션 */}
      <Box bg="blue.600" color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={10} align="center">
            <Tag size="lg" colorScheme="blue" px={6} py={2} borderRadius="full">
              #업계리더가쓰는 #레이블리
            </Tag>
            <Box boxSize="200px">
              <Image
                src="/images/collaboration/rocket.png"
                alt="로켓"
                h="200px"
              />
            </Box>
            <VStack spacing={3} textAlign="center">
              <Text fontSize="xl">단순한 수익 관계가 아닌</Text>
              <Text fontSize="xl">서로의 성장을 돕는 파트너십 추구</Text>
              <Text fontSize="xl">함께 성장하는 파트너</Text>
            </VStack>
          </VStack>
        </Container>
      </Box>

      {/* 8년 업력 섹션 */}
      <Box bg="blue.700" color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="center">
            <Tag colorScheme="blue">#인전성</Tag>
            <Heading size="lg">'18년부터 음원 유통, 8년 업력</Heading>
            <VStack spacing={2} textAlign="center">
              <Text>2018 평창동계패럴림픽 공식음원가 작곡 공모전 주관</Text>
              <Text>올림픽조직위원회 / 대한장애인체육회 주최</Text>
              <Text>공식음원가 발매사</Text>
              <Text fontSize="xl" fontWeight="bold" mt={4}>
                레이블리
              </Text>
            </VStack>
            <SimpleGrid columns={2} spacing={4} mt={8}>
              <Image
                src="/images/olympic1.png"
                alt="올림픽 이미지 1"
                h="300px"
              />
              <Image
                src="/images/olympic2.png"
                alt="올림픽 이미지 2"
                h="300px"
              />
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* 음원 직배급 계약사 섹션 */}
      <Box bg="black" color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={10} align="center">
            <Tag size="lg" colorScheme="gray" px={6} py={2} borderRadius="full">
              #신뢰성
            </Tag>
            <Box boxSize="220px" position="relative">
              <Image
                src="/images/collaboration/star-badge.png"
                alt="신뢰성 뱃지"
                h="220px"
              />
            </Box>
            <VStack spacing={3} textAlign="center">
              <Text fontSize="xl">카카오엔터테인먼트의 멜론,</Text>
              <Text fontSize="xl">지니뮤직의 지니,</Text>
              <Text fontSize="xl">YG Plus의 바이브,</Text>
              <Text fontSize="xl">
                드림어스컴퍼니의 플로 등의 음원 배급 계약사
              </Text>
              <Heading size="lg" mt={4}>
                레이블리
              </Heading>
            </VStack>

            <Image
              src="/images/collaboration/entertainment.PNG"
              alt="배급사"
              h="200px"
            />
          </VStack>
        </Container>
      </Box>

      {/* FAQ 섹션 */}
      <Container maxW="container.xl" py={12}>
        <VStack spacing={8} align="stretch">
          <Heading size="lg" textAlign="center">
            FAQ 자주 묻는 질문
          </Heading>

          <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
            <Text fontSize="lg" fontWeight="bold" mb={2}>
              레이블리의 수익 정산은 몇 % 인가요?
            </Text>
            <Text fontSize="md" color="gray.700" mb={4}>
              레이블리는 소스 크리에이터에게 90% 수익을 정산해드리고 있으며,
              조회수(트래픽)가 높아지면 더 높은 % 로 수익을 정산해드립니다.
              <br />
              <br />
              10%는 레이블리 서비스의 운영과 개선, 음반 제작, 소스 제작 지원
              등에 사용되고 있습니다.
            </Text>
          </Box>

          <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
            <Text fontSize="lg" fontWeight="bold" mb={2}>
              레이블리의 음악은 몇 곡이며, 어떤 장르와 분위기인가요?
            </Text>
            <Text fontSize="md" color="gray.700" mb={4}>
              레이블리는 음악은 매주 약 50곡씩 제작되며, 필요한 음악이 있으신
              경우 장르/분위기에 맞춰 제작해드리고 있습니다.
            </Text>
          </Box>

          <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
            <Text fontSize="lg" fontWeight="bold" mb={2}>
              협력사라는 곳에서 연락을 받았는데, 협력사는 무엇인가요?
            </Text>
            <Text fontSize="md" color="gray.700" mb={4}>
              레이블리는 (주)사노드, 해브앤컴퍼니, 뮤직콜라 3곳과 공식
              파트너(협력) 계약을 체결하고 있습니다. 공식 협력사는 레이블리와
              함께 할 수 있는 소스 크리에이터와 계약 체결 대행을 할 수 있는
              권한을 가지고 있습니다.
            </Text>
          </Box>

          <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
            <Text fontSize="lg" fontWeight="bold" mb={2}>
              협력사를 통한 계약에 따른 불이익은 없나요?
            </Text>
            <Text fontSize="md" color="gray.700" mb={4}>
              전혀 없습니다. 공식 협력사는 레이블리의 수익의 일부를
              쉐어하기때문에 소스 크리에이터가 부담해야하는 부분은 전혀
              없습니다.
            </Text>
          </Box>

          <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
            <Text fontSize="lg" fontWeight="bold" mb={2}>
              레이블리와는 어떻게 연락할 수 있나요?
            </Text>
            <Text fontSize="md" color="gray.700">
              레이블리와는 3가지 방법으로 24시간 연락할 수 있습니다.
            </Text>
            <Text fontSize="md" color="gray.700" mt={2}>
              [1] 유선 연락 : 레이블리 대표 번호(02-2138-2874) 혹은 공식
              협력사로부터 제공받은 연락처로 연락하실 수 있습니다.
            </Text>
            <Text fontSize="md" color="gray.700" mt={2}>
              [2] 카카오톡 : 카카오톡 검색에서 '레이블리'를 검색 후 채널 추가
              후, 카카오톡 메시지를 보낼 수 있습니다.
            </Text>
            <Text fontSize="md" color="gray.700" mt={2}>
              [3] 채널톡 : 레이블리 우측 하단에 보이는 채널톡을 클릭(터치)하신
              후, 메시지를 보낼 수 있습니다.
            </Text>
          </Box>
        </VStack>
      </Container>

      {/* 계약 상담 섹션 */}
      <Container maxW="container.xl" py={12}>
        <VStack
          spacing={6}
          align="center"
          bg="yellow.50"
          p={8}
          borderRadius="xl"
        >
          <Text fontSize="lg">오래 함께 할 수 있는 파트너를 찾습니다</Text>
          <Text fontSize="xl" fontWeight="bold" textAlign="center" maxW="4xl">
            계약 상담 신청하시면 쇼츠 크리에이터 한 분, 한 분 찾아뵈어
            레이블리의 경쟁력을 설명드리고 있습니다
          </Text>
          <Button
            colorScheme="yellow"
            size="lg"
            rounded="full"
            px={12}
            py={7}
            fontSize="xl"
            shadow="lg"
            _hover={{
              transform: "translateY(-2px)",
              shadow: "xl",
            }}
            transition="all 0.2s"
            onClick={onOpen}
          >
            계약 상담 신청하기
          </Button>
        </VStack>
      </Container>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent maxW="lg">
          <ModalHeader>레이블리 이용 계약 문의하기</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4} fontSize="md">
              신규 회원은 개별 계약 후 이용이 가능합니다. 이용 계약 상담을
              신청해주시면, 기재해주신 연락처로 연락드리겠습니다.
              <br />
              (이메일만 기재할 경우, 스팸으로 분류되는 경우가 많습니다. 이용
              계약 문의주시면, 연락처로 연락드립니다.)
            </Text>
            <FormControl mb={3} isRequired>
              <FormLabel>성함 (필수)</FormLabel>
              <Input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="성함"
              />
            </FormControl>
            <FormControl mb={3} isRequired>
              <FormLabel>이메일 (필수)</FormLabel>
              <Input
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="이메일"
                type="email"
              />
            </FormControl>
            <FormControl mb={3} isRequired>
              <FormLabel>연락처 (필수)</FormLabel>
              <Input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="연락처"
              />
            </FormControl>
            <FormControl mb={3} isRequired>
              <FormLabel>운영중인 유튜브 채널 (필수)</FormLabel>
              <Input
                name="channel"
                value={form.channel}
                onChange={handleChange}
                placeholder="유튜브 채널"
              />
            </FormControl>
            <Checkbox
              name="agree"
              isChecked={form.agree}
              onChange={handleChange}
              mb={2}
            >
              (필수) 레이블리 이용 상담을 위한 개인정보 제공에 동의합니다.
            </Checkbox>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={isLoading}
              isDisabled={!isFormValid}
              w="100%"
              size="lg"
            >
              상담 요청하기
            </Button>
          </ModalFooter>
          <Text fontSize="sm" color="gray.500" mb={4} textAlign="center">
            UNEARTH 에 문의하실 회원은 contact@unearth.co.kr로 연락주시길
            바랍니다
          </Text>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CollaborationHome;
